export interface RecoveryDecisionInput {
  /** Whether the locally cached draft has unsaved changes. */
  localIsDirty: boolean;
  /** Epoch ms of the last local (localStorage) save, or null if never saved. */
  localLastSave: number | null;
  /** Epoch ms of the last successful server sync of this draft, or null. */
  localLastServerSync: number | null;
  /** Epoch ms of the server document's last_modified (0 if unknown). */
  serverLastModified: number;
  /**
   * Whether the local draft's content actually differs from the server copy
   * (deep content comparison by the caller). When provided this is the primary
   * signal — a draft whose content matches the server is never recovered or
   * flagged as a conflict, even if its dirty flag/timestamps say otherwise, and
   * a draft that differs is reconciled even when it was previously synced (e.g.
   * the server copy was overwritten independently). When omitted, the decision
   * falls back to the dirty flag + timestamps.
   */
  documentsDiffer?: boolean;
  /** Clock-skew tolerance in ms. */
  graceMs?: number;
}

/**
 * Outcome of comparing a locally cached draft against the server document.
 *
 * - `recover` — the local draft carries genuine edits the server has not seen,
 *   and the server has not advanced past the draft's sync point. Load local.
 * - `server`  — the local draft is stale (or clean); the server is authoritative.
 * - `conflict` — BOTH sides moved independently: the draft has unsynced local
 *   edits AND the server advanced past the draft's sync point. Neither can be
 *   chosen safely, so the user must decide.
 */
export type DraftRecoveryDecision = 'recover' | 'server' | 'conflict';

/**
 * Classify how to reconcile a locally cached (localStorage) draft with the
 * document returned by the server.
 *
 * Data-loss guard (Smartcomtv incident, 2026-06): a stale local draft must NOT
 * silently win over a server copy that has moved on independently, and — the
 * other side of the same coin — genuine local edits must NOT be silently
 * discarded just because the server also changed. When both have advanced we
 * return `conflict` so the editor can ask the user instead of guessing.
 */
export function resolveDraftRecovery({
  localIsDirty,
  localLastSave,
  localLastServerSync,
  serverLastModified,
  documentsDiffer,
  graceMs = 5000
}: RecoveryDecisionInput): DraftRecoveryDecision {
  const lastSave = localLastSave ?? 0;
  const lastSync = localLastServerSync ?? 0;
  const server = serverLastModified || 0;

  // Genuine unsynced local edits made after the last successful sync.
  const hasUnsyncedLocalEdits = localIsDirty && lastSave > lastSync;
  // Local copy is clearly newer than the server (e.g. server timestamp missing).
  const localNewerThanServer = lastSave > server + graceMs;

  // Does the local draft actually differ from the server copy? Prefer the
  // caller's deep content comparison; otherwise infer it from the dirty flag
  // and timestamps. If there is no real difference, the server copy is fine —
  // never prompt or recover when the two are identical.
  const differs =
    documentsDiffer ?? (hasUnsyncedLocalEdits || localNewerThanServer);
  if (!differs) {
    return 'server';
  }

  // The local draft differs from the server. Did the server also change
  // out-of-band since this draft last synced (another tab/device, a
  // regeneration, or an admin restore)?
  const baseline = localLastServerSync != null ? lastSync : lastSave;
  const serverMovedAhead = server > baseline + graceMs;

  // Both sides moved → neither can be chosen automatically, ask the user.
  // Only the local side moved → keep the local draft.
  return serverMovedAhead ? 'conflict' : 'recover';
}

/**
 * Back-compat boolean wrapper around {@link resolveDraftRecovery}: true only
 * when the local draft should be loaded outright. A `conflict` resolves to
 * false here (the caller must handle conflicts explicitly via
 * {@link resolveDraftRecovery}).
 */
export function shouldRecoverLocalDraft(input: RecoveryDecisionInput): boolean {
  return resolveDraftRecovery(input) === 'recover';
}
